import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '../components/ui/button';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { trackPaymentStart, trackPaymentSuccess } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ enquiryId, amount, pricingOption }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (error) {
        toast({
          title: "Payment Failed",
          description: error.message,
          variant: "destructive"
        });
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Confirm payment on backend
        await axios.post(`${API}/payments/confirm-payment`, null, {
          params: { payment_intent_id: paymentIntent.id }
        });
        
        // Track successful payment in GA4
        trackPaymentSuccess(amount, pricingOption, enquiryId);
        
        setPaymentSuccess(true);
        toast({
          title: "Payment Successful!",
          description: "Your session has been booked. We'll contact you within 24 hours.",
        });
        
        // Redirect to home after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: "Payment Error",
        description: "There was an error processing your payment. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (paymentSuccess) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'var(--accent-wash)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 1.5rem'
        }}>
          <CheckCircle2 size={48} color="var(--accent-text)" />
        </div>
        <h2 className="heading-2" style={{ marginBottom: '1rem' }}>Payment Successful!</h2>
        <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
          Thank you for booking with Abba Academy. We'll be in touch shortly to confirm your session details.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: '500px', margin: '0 auto' }}>
      <PaymentElement />
      <Button
        type="submit"
        disabled={!stripe || isProcessing}
        className="btn-primary"
        style={{ width: '100%', marginTop: '1.5rem' }}
      >
        {isProcessing ? 'Processing...' : `Pay £${(amount / 100).toFixed(2)}`}
      </Button>
    </form>
  );
};

const PaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const enquiryId = searchParams.get('enquiryId');
  const amount = parseInt(searchParams.get('amount')) || 9999; // Default to monthly
  const pricingOption = searchParams.get('option') || 'monthly';
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const getOptionLabel = () => {
    if (pricingOption === 'monthly') {
      return 'Monthly Package (4 Lessons) - £99.99';
    } else if (pricingOption === 'vip') {
      return 'VIP 1-on-1 Private Tuition - £65.00';
    }
    return 'One-Off Lesson - £29.99';
  };

  useEffect(() => {
    if (!enquiryId) {
      setError('No enquiry ID provided');
      setLoading(false);
      return;
    }

    // Create payment intent
    const createPaymentIntent = async () => {
      try {
        // Track payment initiation in GA4
        trackPaymentStart(amount, pricingOption);
        
        const response = await axios.post(`${API}/payments/create-payment-intent`, {
          enquiry_id: enquiryId,
          amount: amount,
          currency: 'gbp'
        });

        if (response.data.success) {
          setClientSecret(response.data.client_secret);
        } else {
          setError('Failed to initialize payment');
        }
      } catch (err) {
        console.error('Payment intent error:', err);
        setError(err.response?.data?.detail || 'Failed to initialize payment');
      } finally {
        setLoading(false);
      }
    };

    createPaymentIntent();
  }, [enquiryId, amount, pricingOption]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="body-large">Loading payment...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ textAlign: 'center', maxWidth: '500px' }}>
          <h2 className="heading-2" style={{ marginBottom: '1rem', color: 'var(--destructive)' }}>Payment Error</h2>
          <p className="body-large" style={{ marginBottom: '2rem' }}>{error}</p>
          <Button onClick={() => navigate('/')} className="btn-primary">
            <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#40E0D0',
        colorText: '#0e0f0c',
        fontFamily: 'system-ui, sans-serif',
        borderRadius: '8px',
      },
    },
  };

  return (
    <div style={{ minHeight: '100vh', padding: '2rem', background: 'var(--bg-section)' }}>
      <div className="container" style={{ maxWidth: '800px', paddingTop: '2rem' }}>
        <Button 
          onClick={() => navigate('/')} 
          className="btn-secondary"
          style={{ marginBottom: '2rem' }}
        >
          <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} />
          Back to Home
        </Button>

        <div className="product-card" style={{ padding: '2rem' }}>
          <h1 className="heading-2" style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
            Complete Your Booking
          </h1>
          <p className="body-large" style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '2rem' }}>
            {getOptionLabel()}
          </p>

          {clientSecret && (
            <Elements stripe={stripePromise} options={options}>
              <CheckoutForm enquiryId={enquiryId} amount={amount} pricingOption={pricingOption} />
            </Elements>
          )}
        </div>

        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-light)',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
            🔒 Secure payment powered by Stripe. Your payment information is encrypted and secure.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
